import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Play, Heart, Instagram } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { CREATORS } from "@/data/site";
import { PRODUCTS } from "@/data/products";
import { Reveal, StaggerItem } from "@/components/Reveal";
import Stamp from "@/components/Stamp";
import Seo, { breadcrumbLd } from "@/components/Seo";
import { getCreatorSeo } from "@/lib/seo";

const CreatorDetail = () => {
  const { slug } = useParams();
  const { t, tr } = useLang();
  const creator = CREATORS.find((c) => c.slug === slug);
  if (!creator) return <Navigate to="/creators" replace />;

  const seo = getCreatorSeo(creator.slug);

  const fightProduct = PRODUCTS.find((p) => tr(p.name) === tr(creator.fights));
  const others = CREATORS.filter((c) => c.slug !== slug);
  const likes = ["12.4K", "8.9K", "21.1K"];

  return (
    <div data-testid="page-creator-detail">
      <Seo
        title={seo.title}
        description={seo.description}
        path={seo.path}
        image={seo.image}
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Creators", path: "/creators" },
            { name: creator.name, path: `/creator/${creator.slug}` },
          ]),
        ]}
      />
      {/* Hero */}
      <section className="bg-peach border-b border-ink py-10 md:py-16">
        <div className="max-w-[1440px] mx-auto px-4 md:px-8">
          <Link to="/creators" className="inline-flex items-center gap-2 kicker hover:text-pink transition-colors" data-testid="creator-back">
            <ArrowLeft className="w-4 h-4" strokeWidth={2} /> {t.creatorPage.back}
          </Link>

          <div className="grid lg:grid-cols-2 border border-ink mt-6">
            <Reveal className="border-b lg:border-b-0 lg:border-r border-ink">
              <div className="relative aspect-[4/5] overflow-hidden">
                <img src={creator.image} alt={creator.name} className="w-full h-full object-cover" />
                <div className="absolute top-4 right-4"><Stamp size={80} bg="bg-cream" /></div>
                <span className="absolute bottom-4 left-4 kicker px-3 py-1 border border-ink rounded-full text-ink" style={{ backgroundColor: creator.color }}>
                  {tr(creator.role)}
                </span>
              </div>
            </Reveal>

            <Reveal delay={0.1} className="p-6 md:p-10 bg-cream flex flex-col justify-center">
              <p className="kicker" style={{ color: creator.color }}>{creator.handle} · {creator.followers}</p>
              <h1 className="display uppercase text-6xl md:text-7xl leading-[0.84] mt-2 text-brand">{creator.name}</h1>
              <p className="mt-5 display uppercase text-xl md:text-2xl leading-tight">“{tr(creator.quote)}”</p>
              <div className="mt-7 flex flex-wrap gap-3">
                <a href="#" onClick={(e) => e.preventDefault()} className="btn bg-ink text-cream h-12 px-6 hover:bg-pink">
                  <Instagram className="w-4 h-4" strokeWidth={2} /> {t.creatorPage.follow}
                </a>
                {fightProduct && (
                  <Link to={`/product/${fightProduct.id}`} className="btn-accent h-12 px-6">
                    {t.creatorPage.shopFight} <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
                  </Link>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="bg-cream border-b border-ink py-14 md:py-20">
        <div className="max-w-[900px] mx-auto px-4 md:px-8">
          <Reveal>
            <h2 className="display uppercase text-4xl md:text-6xl text-brand">{t.creatorPage.story}</h2>
            <p className="mt-5 text-xl md:text-2xl font-medium leading-relaxed text-ink/80">{tr(creator.story)}</p>
            <div className="mt-6 inline-flex items-center gap-2 kicker px-4 py-2 rounded-full border border-ink" style={{ backgroundColor: creator.color }}>
              {t.creatorPage.fights}: {tr(creator.fights)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Reels */}
      <section className="bg-sand border-b border-ink py-14 md:py-20">
        <div className="max-w-[1440px] mx-auto px-4 md:px-8">
          <Reveal><h2 className="display uppercase text-4xl md:text-6xl text-brand mb-8">{t.creatorPage.reels}</h2></Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-ink border border-ink">
            {creator.reels.map((img, i) => (
              <StaggerItem key={i} index={i}>
                <div className="group relative overflow-hidden aspect-[9/16] bg-sand" data-testid={`reel-${i}`}>
                  <img src={img} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/50 to-transparent" />
                  <div className="absolute top-3 left-3 w-11 h-11 rounded-full bg-cream/95 border border-ink flex items-center justify-center">
                    <Play className="w-5 h-5 text-ink fill-ink ml-0.5" />
                  </div>
                  <div className="absolute bottom-3 left-3 flex items-center gap-1.5 text-white font-bold text-sm">
                    <Heart className="w-4 h-4 fill-pink text-pink" /> {likes[i % likes.length]}
                  </div>
                  <span className="absolute top-3 right-3 kicker text-[10px] bg-lime border border-ink rounded-full px-2 py-0.5">Reel</span>
                </div>
              </StaggerItem>
            ))}
          </div>
        </div>
      </section>

      {/* More creators */}
      <section className="bg-cream py-14 md:py-20">
        <div className="max-w-[1440px] mx-auto px-4 md:px-8">
          <h2 className="display uppercase text-3xl md:text-5xl text-brand mb-8">{t.creatorPage.more}</h2>
          <div className="grid grid-cols-3 gap-px bg-ink border border-ink">
            {others.map((c) => (
              <Link key={c.slug} to={`/creator/${c.slug}`} className="group bg-cream block" data-testid={`more-creator-${c.slug}`}>
                <div className="relative aspect-square overflow-hidden bg-sand border-b border-ink">
                  <img src={c.image} alt={c.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                </div>
                <div className="p-3.5">
                  <p className="display uppercase text-base md:text-xl leading-none truncate">{c.name}</p>
                  <p className="text-ink/50 text-xs md:text-sm">{c.handle}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CreatorDetail;
